import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, CalendarPlus, User, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useInstalledState } from "../lib/useInstalledState";
import { promptInstall, useInstallable } from "../lib/pwaInstall";
import InstallDrawer from "./InstallDrawer";

/**
 * iOS-style sticky bottom tab bar for mobile + tablet (hidden on desktop).
 *
 * Tabs: Home / Book / Portal / Install-app
 *  - The Install tab opens an InstallDrawer that auto-detects the device
 *    (iOS / Android / Desktop) and triggers the appropriate install flow.
 *  - When the app is already installed, the tab flips to a green
 *    CheckCircle + "Installed" label.
 *  - Hidden on /admin/* routes.
 */
export default function MobileTabBar() {
  const { t } = useI18n();
  const location = useLocation();
  const [installOpen, setInstallOpen] = useState(false);
  const installed = useInstalledState();
  const canPromptNatively = useInstallable();

  // Hide on admin pages
  if (location.pathname.startsWith("/admin")) return null;

  const handleInstallTap = async () => {
    navigator.vibrate?.(10);
    if (installed) {
      // Already installed — open the drawer to confirm the success state.
      setInstallOpen(true);
      return;
    }
    if (canPromptNatively) {
      // Direct, in-page native install dialog (Android & Desktop Chrome/Edge).
      const outcome = await promptInstall();
      if (outcome === "unavailable") setInstallOpen(true);
      else if (outcome === "accepted") {
        toast.success(t("install.installed_toast"));
      }
      return;
    }
    // No native API available (iOS Safari, browsers without prompt support):
    // open the drawer with platform-specific step-by-step instructions.
    setInstallOpen(true);
  };

  const isActive = (test) => {
    if (test === "/") return location.pathname === "/";
    if (test === "/book") return location.pathname.startsWith("/book");
    if (test === "/portal") return location.pathname.startsWith("/portal");
    return false;
  };

  return (
    <>
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-[100] tab-bar-enter"
        data-testid="mobile-tab-bar"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="bg-white/85 backdrop-blur-xl border-t border-slate-200/70 supports-[backdrop-filter]:bg-white/75">
          <div className="flex items-stretch justify-between px-2 pt-1.5 pb-1.5 h-16">
            <TabLink
              to="/"
              icon={Home}
              label={t("nav.home")}
              active={isActive("/")}
              testId="mobile-tab-home"
            />
            <TabLink
              to="/book"
              icon={CalendarPlus}
              label={t("nav.book_short")}
              active={isActive("/book")}
              testId="mobile-tab-book"
            />
            <TabLink
              to="/portal"
              icon={User}
              label={t("nav.portal_short")}
              active={isActive("/portal")}
              testId="mobile-tab-portal"
            />
            <TabButton
              icon={installed ? CheckCircle2 : Download}
              label={
                installed ? t("nav.installed_short") : t("nav.install_short")
              }
              active={installOpen}
              installedTone={installed}
              onClick={handleInstallTap}
              testId={installed ? "mobile-tab-installed" : "mobile-tab-install"}
            />
          </div>
        </div>
      </nav>

      <InstallDrawer open={installOpen} onOpenChange={setInstallOpen} />
    </>
  );
}

function TabLink({ to, icon: Icon, label, active, testId }) {
  return (
    <Link
      to={to}
      onClick={() => navigator.vibrate?.(10)}
      className="flex-1 flex flex-col items-center justify-center gap-0.5 rounded-xl py-1 transition-all"
      data-testid={testId}
    >
      <Icon
        className={`w-6 h-6 transition-all ${
          active ? "text-sky-600 scale-110" : "text-slate-400"
        }`}
        strokeWidth={active ? 2.4 : 2}
      />
      <span
        className={`text-[10px] font-medium leading-tight tracking-tight ${
          active ? "text-sky-600" : "text-slate-400"
        }`}
      >
        {label}
      </span>
    </Link>
  );
}

function TabButton({ icon: Icon, label, active, installedTone, onClick, testId }) {
  const accent = installedTone
    ? "text-emerald-600"
    : active
      ? "text-sky-600"
      : "text-slate-400";
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 flex flex-col items-center justify-center gap-0.5 rounded-xl py-1 transition-all"
      data-testid={testId}
    >
      <Icon
        className={`w-6 h-6 transition-all ${accent} ${
          active && !installedTone ? "scale-110" : ""
        }`}
        strokeWidth={active || installedTone ? 2.4 : 2}
      />
      <span
        className={`text-[10px] font-medium leading-tight tracking-tight ${accent}`}
      >
        {label}
      </span>
    </button>
  );
}
